import { useEffect, useRef, type ReactNode } from 'react';
import { AppProvider, useApp } from './state/store';
import { useRoute } from './lib/router';
import { BottomNav, Toast } from './components/chrome';
import { Sheets } from './sheets/Sheets';
import { Home } from './screens/Home';
import { Discover } from './screens/Discover';
import { Calendar } from './screens/Calendar';
import { Create } from './screens/Create';
import { Profile } from './screens/Profile';
import { ActivityScreen } from './screens/Activity';
import { Collaboration } from './screens/Collaboration';
import { ShareKitScreen } from './screens/ShareKit';
import { AuthGate } from './auth/AuthGate';
import { SessionCheck } from './screens/SignIn';
import { Btn } from './components/ui';
import { C, font } from './theme/tokens';
import { isDemo } from './lib/clock';

/** Api mode: screens render only server data. Loading → the session screen; failure → retry. Never the seed. */
function DataGate({ children }: { children: ReactNode }) {
  const { server, reload } = useApp();
  useEffect(() => {
    if (!isDemo && server === 'idle') void reload();
  }, [server, reload]);
  if (isDemo || server === 'ready') return <>{children}</>;
  if (server === 'error') {
    return (
      <div className="signin-check" role="alert">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center', maxWidth: 320, color: C.white }}>
          <span style={{ font: font(700, 22, 1.2) }}>Could not load UGAIN.</span>
          <span style={{ font: font(400, 14, 1.45), color: C.mutedDark }}>The UGAIN server did not answer. Nothing is shown until it does.</span>
          <Btn variant="primary" onClick={() => void reload()}>Try again</Btn>
        </div>
      </div>
    );
  }
  return <SessionCheck />;
}

function Screen() {
  const { route } = useRoute();
  switch (route.name) {
    case 'home': return <Home />;
    case 'discover': return <Discover />;
    case 'calendar': return <Calendar />;
    case 'create': return <Create sub={route.sub} />;
    case 'me': return <Profile />;
    case 'activity': return <ActivityScreen id={route.id} />;
    case 'profile': return <Profile id={route.id} />;
    case 'collab': return <Collaboration id={route.id} />;
    case 'share': return <ShareKitScreen id={route.id} />;
  }
}

function Shell() {
  const { path } = useRoute();
  const main = useRef<HTMLElement>(null);
  useEffect(() => {
    main.current?.scrollTo({ top: 0 });
  }, [path]);
  return (
    <div className="app-frame">
      <div className="app-col">
        <main ref={main} key={path} className="noscroll app-scroll">
          <Screen />
        </main>
        <BottomNav />
        <Toast />
        <Sheets />
      </div>
    </div>
  );
}

export function App() {
  return (
    <AppProvider>
      <AuthGate>
        <DataGate>
          <Shell />
        </DataGate>
      </AuthGate>
    </AppProvider>
  );
}
